/// <amd-module name="Reporting/Visuals/VisualMessages/VisualMessageBase" />
import * as React from "react";
import { FPSLink, IFPSLinkProps } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { VisualTitle } from "../VisualTitle";
import { ImageProps, WrappedVisualImage } from "./WrappedVisualImage";

export interface VisualMessageAction {
    /** Text of a link button for users to click to solve the problem. */
    text: string;

    /** Aria label for the action link. */
    ariaLabel: string;

    /**
     * Callback when the user clicks the action.
     * The use of onClick should be reserved for either logging telemetry
     * or for navigation that cannot be achieved through href. Navigation behavior
     * implemented in this handler can pre-empt an href through event.preventDefault().
     */
    onClick?: (event: React.SyntheticEvent<any>) => void;

    /**
     * Href if the action is simply a link.
     * The use of href is for when the action is just
     * navigation to a link.
     */
    href?: string;
}

/**
 * Size, expressed in pixels.
 */
export interface Size {
    width: number;
    height: number;
}

export interface CommonMessageProps {}

export interface IWidgetMessageProps extends IVssComponentProperties {
    /** The title on the card, usually the title of the visual or widget */
    visualTitle: string;

    /** The message to display about the problem */
    messageContent: string;

    /** The size the widget */
    size: Size;

    /** Path to a custom Image */
    imagePath?: string;

    /** Alt text for the custom image. */
    altText?: string;

    /** Optional action for the user to take. */
    action?: VisualMessageAction;

    /** Optional parameter to force rendering a small image in case of rectangular widgets */
    renderSmallImage?: boolean;

    /** Hides the title text from rendering , this is currently only used by the embedded report scenarios*/
    hideTitle?: boolean;
}

/** (Implementation Class) Displays a message about the state of a Visual or Widget. Wrapped by scenario centric concrete types. */
export class VisualMessageBase extends VssComponent<IWidgetMessageProps, {}> {
    /** Describes if widget should prefer a small image due to limited form factor. */
    public static useSmallImage(size: Size): boolean {
        return size.height <= 160 && size.width <= 160;
    }

    /** Describes if a widget is a small square, or sufficiently large to comfortably house an image. Awkward rectangular sizes with one small dimension omit icon. */
    public static sizeSupportsImage(size: Size): boolean {
        return VisualMessageBase.useSmallImage(size) || (size.height > 160 && size.width > 160);
    }

    constructor(props: IWidgetMessageProps) {
        super(props);
    }

    public render(): JSX.Element {
        return (
            <div className={"visual-message-card-container"} style={this.props.size}>
                <div className={"visual-message-base"}>
                    <VisualTitle text={this.props.visualTitle} suppressDefaultPadding={true} hideTitle={this.props.hideTitle} />
                    <div className="message-body">
                        {this.renderContent()}
                        {this.renderImage()}
                        {this.renderLink(this.props.action)}
                    </div>
                </div>
            </div>
        );
    }

    private renderContent(): JSX.Element | null {
        // bypass content line in favor of action link for extremely constrained experiences.
        if (this.props.size.height > 160 || this.props.size.width > 160 || !this.hasAction(this.props.action)) {
            return <div className="message-content">{this.props.messageContent}</div>;
        }
        return null;
    }

    /** Used to determine if the is an action to render. */
    private hasAction(action: VisualMessageAction | undefined): boolean {
        return action != null && (action.text != null || action.onClick != null || action.href != null);
    }

    private renderLink(action: VisualMessageAction | undefined): JSX.Element | null {
        if (action != null && this.hasAction(action)) {
            const linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement> & IFPSLinkProps = {
                href: action.href,
                className: "link",
                ariaLabel: action.ariaLabel
            };

            if (action.onClick) {
                linkProps.onClick = action.onClick;
            }

            return (
                <div className="message-action">
                    <FPSLink {...linkProps}>{action.text}</FPSLink>
                </div>
            );
        }
        return null;
    }

    private renderImage(): JSX.Element | null {
        if (this.props.imagePath && (this.props.renderSmallImage || VisualMessageBase.sizeSupportsImage(this.props.size))) {
            const imageProps: ImageProps = {
                imagePath: this.props.imagePath,
                includeDependencies: ["ms.vss-reporting.visual-messages-shared-data"],
                altText: this.props.altText
            };

            if (VisualMessageBase.useSmallImage(this.props.size) || this.props.renderSmallImage) {
                imageProps.height = 40;
                imageProps.width = 40;
            }
            return (
                <div className="message-icon">
                    <WrappedVisualImage {...imageProps} />
                </div>
            );
        }
        return null;
    }
}
